import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { pget, pput, ppost, pdel } from "./portalApi";
import { ExtraNumbers, residentPhoneEndpoints } from "@/components/ExtraNumbers";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail, MessageSquare, Phone, CheckCircle2, ShieldCheck, Lock, ShieldAlert, ChevronDown } from "lucide-react";

// One row per notification event; Email + SMS toggles are independent per row so residents can
// choose exactly how they hear about each thing. Urgent safety keeps its SMS locked ON.
function EventRow({ entry, verified, optedOut, onToggle }) {
  const v = entry.value || { email: false, sms: false };
  return (
    <div className="flex items-start gap-4 py-3.5 border-b border-sand-100 last:border-b-0" data-testid={`resnotif-row-${entry.key}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink">{entry.label}</div>
        <div className="text-sm text-muted-foreground mt-0.5 leading-snug">{entry.description}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0 pt-1">
        <label className="flex items-center gap-1.5 cursor-pointer" title="Email">
          <Mail className="h-4 w-4 text-ink/40" strokeWidth={2} />
          {entry.lock_email ? (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5"><Lock className="h-2.5 w-2.5" /> On</span>
          ) : (
            <Switch data-testid={`resnotif-${entry.key}-email`}
              checked={!!v.email} disabled={optedOut}
              onCheckedChange={(nv) => onToggle(entry.key, "email", nv)} />
          )}
        </label>
        <label className={`flex items-center gap-1.5 cursor-pointer ${!verified && !entry.lock_sms ? "opacity-40" : ""}`}
          title={entry.lock_sms ? "Urgent safety — text alerts always on for your safety" : (verified ? "Text me" : "Verify your mobile number to enable text alerts")}>
          <MessageSquare className="h-4 w-4 text-ink/40" strokeWidth={2} />
          {entry.lock_sms ? (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5"><Lock className="h-2.5 w-2.5" /> On</span>
          ) : (
            <Switch data-testid={`resnotif-${entry.key}-sms`}
              checked={!!v.sms} disabled={!verified || optedOut}
              onCheckedChange={(nv) => onToggle(entry.key, "sms", nv)} />
          )}
        </label>
      </div>
    </div>
  );
}

// Recent activity: the same announcements + request updates the bell counts, so a resident
// can actually read what the badge is pointing at (iter252/L10). Collapsible via chevron
// (iter270) — residents can tuck it away and the choice is remembered.
function ActivityFeed() {
  const [feed, setFeed] = useState(null);
  const [marking, setMarking] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("portal-activity-collapsed") === "1"; } catch { return false; }
  });

  const load = () => pget("/resident/notifications").then((r) => setFeed(r.data)).catch(() => setFeed({ items: [] }));
  useEffect(() => { load(); }, []);

  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("portal-activity-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  const markRead = async () => {
    setMarking(true);
    try { await ppost("/resident/notifications/read"); await load(); toast.success("All caught up"); }
    catch { toast.error("Couldn't mark these as read"); }
    finally { setMarking(false); }
  };

  if (!feed) return null;
  const items = feed.items || [];

  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-5" data-testid="portal-activity-feed">
      <div className="flex items-center justify-between gap-3">
        <button data-testid="portal-activity-toggle" onClick={toggleCollapsed} aria-expanded={!collapsed}
          className="group flex items-center gap-2 min-w-0 flex-1 text-left">
          <ChevronDown className={`h-4 w-4 text-ink/40 shrink-0 transition-transform duration-200 group-hover:text-ink/70 ${collapsed ? "-rotate-90" : ""}`} strokeWidth={2.5} />
          <h2 className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground group-hover:text-ink/70 transition-colors">Recent activity</h2>
          {collapsed && feed.unread > 0 && (
            <span data-testid="portal-activity-collapsed-badge" className="rounded-full bg-redline-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none">{feed.unread}</span>
          )}
        </button>
        {!collapsed && feed.unread > 0 && (
          <button data-testid="portal-activity-mark-read" onClick={markRead} disabled={marking}
            className="text-xs font-semibold text-redline-600 hover:text-redline-700 disabled:opacity-50 shrink-0">
            Mark all read ({feed.unread})
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="mt-3">
          {items.length === 0 ? (
            <p data-testid="portal-activity-empty" className="text-sm text-muted-foreground py-2">
              Nothing yet. Community announcements and updates to your requests will show up here.
            </p>
          ) : (
            <ul className="divide-y divide-sand-100">
              {items.map((it) => (
                <li key={it.id} data-testid={`portal-activity-${it.id}`} className="py-3 flex items-start gap-3">
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${it.kind === "ticket" ? "bg-blue-400" : it.urgent ? "bg-danger-500" : "bg-emerald-400"}`} />
                  <Link to={it.to || "/portal"} className="min-w-0 flex-1 group">
                    <div className="font-semibold text-ink text-sm group-hover:text-redline-600 transition-colors truncate">{it.title}</div>
                    {it.body && <div className="text-sm text-muted-foreground leading-snug mt-0.5">{it.body}</div>}
                    <div className="text-[11px] text-ink/40 mt-1">{it.at ? new Date(it.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Quiet hours: the resident's own "don't text me at night" window, in the community's local
// time. The backend has always stored and honoured this (`notif_prefs.quiet_hours`), but the
// portal had no control for it — so residents couldn't set the thing that now holds a
// "someone's on the way" text until morning. Email is never held.
function QuietHours({ prefs, onSave }) {
  const qh = prefs.quiet_hours || { enabled: false, start: "21:00", end: "07:00" };
  const [saving, setSaving] = useState(false);
  const set = async (patch) => {
    setSaving(true);
    await onSave({ ...qh, ...patch });
    setSaving(false);
  };
  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-5" data-testid="notify-quiet-hours-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Quiet hours</h2>
          <p className="text-sm text-muted-foreground mt-1.5 leading-snug">
            Hold texts overnight. Emails still arrive, and anything urgent — a safety alert, or an
            urgent job at your home — always comes through.
          </p>
        </div>
        <Switch data-testid="notify-quiet-hours-toggle" checked={!!qh.enabled} disabled={saving}
          onCheckedChange={(v) => set({ enabled: v })} />
      </div>
      {qh.enabled && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-ink/55">From</span>
            <input type="time" data-testid="notify-quiet-hours-start" value={qh.start || "21:00"} disabled={saving}
              onChange={(e) => set({ start: e.target.value })}
              className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-ink/55">until</span>
            <input type="time" data-testid="notify-quiet-hours-end" value={qh.end || "07:00"} disabled={saving}
              onChange={(e) => set({ end: e.target.value })}
              className="rounded-xl border border-sand-200 px-3 py-2 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </label>
          <span className="text-xs text-ink/40">your community&rsquo;s local time</span>
        </div>
      )}
    </div>
  );
}

export default function PortalNotifications() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    pget("/resident/notification-prefs")
      .then((r) => setPrefs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => clearInterval(timerRef.current);
  }, []);

  const startCooldown = (s) => {
    setCooldown(s);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; }), 1000);
  };

  const toggleEvent = async (key, channel, value) => {
    // Optimistic bump the switch so residents see immediate feedback.
    setPrefs((p) => {
      if (!p) return p;
      const next = { ...p, categories: { ...p.categories } };
      Object.keys(next.categories).forEach((cat) => {
        next.categories[cat] = next.categories[cat].map((e) =>
          e.key === key ? { ...e, value: { ...e.value, [channel]: value } } : e);
      });
      return next;
    });
    try {
      const r = await pput("/resident/notification-prefs", { events: { [key]: { [channel]: value } } });
      setPrefs(r.data);
      toast.success(`Saved · ${channel === "email" ? "Email" : "Text"} ${value ? "on" : "off"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
      pget("/resident/notification-prefs").then((r) => setPrefs(r.data)).catch(() => {});
    }
  };

  const saveQuietHours = async (qh) => {
    try {
      const r = await pput("/resident/notification-prefs", { quiet_hours: qh });
      setPrefs(r.data);
      toast.success(qh.enabled ? `Quiet hours ${qh.start}–${qh.end}` : "Quiet hours off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that"); }
  };

  const sendCode = async () => {
    setBusy("send");
    try {
      const r = await ppost("/resident/phone/send-code", { phone: phoneInput });
      setCodeSent(true); startCooldown(r.data.cooldown || 45);
      toast[r.data.sent ? "success" : "message"](r.data.sent ? "Code texted to your phone" : "We tried to text that number — enter the code if it arrives.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send a code."); }
    setBusy("");
  };

  const verifyCode = async () => {
    setBusy("verify");
    try {
      await ppost("/resident/phone/verify", { code });
      const r = await pget("/resident/notification-prefs");
      setPrefs(r.data); setCodeSent(false); setChanging(false); setCode(""); setPhoneInput("");
      toast.success("Number verified — text alerts unlocked.");
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed."); }
    setBusy("");
  };

  const categories = useMemo(() => Object.entries(prefs?.categories || {}), [prefs]);
  const [closedCats, setClosedCats] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("portal.notifcats.closed") || "[]")); } catch { return new Set(); }
  });
  const toggleCat = (cat) => setClosedCats((prev) => {
    const next = new Set(prev);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    try { localStorage.setItem("portal.notifcats.closed", JSON.stringify([...next])); } catch { /* ignore */ }
    return next;
  });

  if (loading || !prefs) return (
    <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
  );

  const verified = prefs.phone_verified;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-notifications-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Bell className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Notifications
        </h1>
        <p className="text-muted-foreground mt-1">Pick email or text — for every kind of alert, independently. Urgent safety texts always come through.</p>
      </div>

      {prefs.opted_out && (
        <div data-testid="notify-optout-banner" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          <span className="font-bold">You&rsquo;ve opted out of all texts</span> (you replied STOP). Your carrier blocks everything until
          you text <span className="font-bold">START</span> to {prefs.community_number || "your community's number"} — then you can fine-tune these.
        </div>
      )}

      {/* iter252/L10 — this page used to be preferences ONLY, while the bell showed "9+"
          unread with nowhere to actually read them. The feed lives here now. */}
      <ActivityFeed />

      {/* HOW we reach you */}
      <div className="rounded-2xl border border-sand-200 bg-white p-5" data-testid="notify-channels-card">
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-3">How we reach you</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Mail className="h-4.5 w-4.5 text-ink/50 shrink-0" strokeWidth={2} />
            <span className="text-sm text-ink/60 w-14 shrink-0">Email</span>
            <span className="font-semibold text-ink truncate flex-1" data-testid="notify-header-email">{prefs.email || "no email on file"}</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="h-4.5 w-4.5 text-ink/50 shrink-0" strokeWidth={2} />
            <span className="text-sm text-ink/60 w-14 shrink-0">Text</span>
            {verified && !changing ? (
              <>
                <span className="font-semibold text-ink truncate flex-1" data-testid="notify-header-phone">{prefs.phone}</span>
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5"><CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} /> Verified</span>
                <button data-testid="notify-phone-change" onClick={() => { setChanging(true); setCodeSent(false); }} className="text-xs font-semibold text-redline-600 hover:text-redline-700">Change</button>
              </>
            ) : (
              <span className="text-ink/50 italic flex-1">Verify a number to unlock text alerts</span>
            )}
          </div>
        </div>

        {(!verified || changing) && !prefs.opted_out && (
          <div className="mt-4 rounded-xl border border-sand-200 bg-sand-50/60 p-3.5">
            <label className="text-sm font-semibold flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-redline-500" /> Mobile number</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="notify-phone-input" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} disabled={busy === "send"}
                placeholder="+15125550123" className="flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid="notify-phone-send-code" onClick={sendCode} disabled={!phoneInput || busy === "send" || cooldown > 0}
                className="rounded-xl bg-ink text-white text-sm font-semibold px-4 py-2.5 hover:bg-ink-700 transition-colors disabled:opacity-50 whitespace-nowrap w-28 shrink-0 text-center tabular-nums">
                {busy === "send" ? "Sending…" : cooldown > 0 ? `Resend ${cooldown}s` : codeSent ? "Resend" : "Send code"}
              </button>
            </div>
            {codeSent && (
              <div className="mt-3">
                <label className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-redline-500" /> Enter the 6-digit code</label>
                <div className="flex gap-2 mt-1.5">
                  <input data-testid="notify-phone-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric"
                    placeholder="123456" className="flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm tracking-[0.3em] font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
                  <button data-testid="notify-phone-verify-btn" onClick={verifyCode} disabled={code.length !== 6 || busy === "verify"}
                    className="rounded-xl bg-redline-500 text-white text-sm font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50 whitespace-nowrap">
                    {busy === "verify" ? "Verifying…" : "Verify"}
                  </button>
                </div>
              </div>
            )}
            {changing && (
              <button onClick={() => { setChanging(false); setCodeSent(false); }} className="text-xs text-muted-foreground hover:text-ink mt-2">Cancel</button>
            )}
          </div>
        )}

        {/* iter378 — additional numbers: each verified with its own code; the concierge recognises
            you from any of them. The primary above is the one it texts. */}
        {!prefs.opted_out && (
          <div className="mt-4 pt-3 border-t border-sand-200" data-testid="notify-extra-numbers">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 mb-2">Other numbers we know you by</div>
            <ExtraNumbers endpoints={residentPhoneEndpoints(pget, ppost, pdel)} testid="res-phones" showPrimary={false}
              onChanged={() => pget("/resident/notification-prefs").then((r) => setPrefs(r.data)).catch(() => {})} />
          </div>
        )}
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-3 -mb-1">
        <div className="flex-1 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40">Notify me about</div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="w-[52px] text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 text-center flex items-center gap-1 justify-center"><Mail className="h-3 w-3" /> Email</span>
          <span className="w-[52px] text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 text-center flex items-center gap-1 justify-center"><MessageSquare className="h-3 w-3" /> Text</span>
        </div>
      </div>

      {categories.map(([cat, rows]) => (
        <section key={cat} data-testid={`resnotif-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
          className="rounded-2xl border border-sand-200 bg-white px-5 py-4">
          <button type="button" data-testid={`resnotif-cat-${cat.toLowerCase().replace(/\s+/g, "-")}-toggle`}
            onClick={() => toggleCat(cat)} aria-expanded={!closedCats.has(cat)}
            className="group flex w-full items-center gap-2 border-b border-sand-100 pb-2 text-left">
            <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform duration-200 group-hover:text-ink/70 ${closedCats.has(cat) ? "-rotate-90" : ""}`} strokeWidth={2.5} />
            <h3 className="font-heading text-sm font-bold text-ink">{cat}</h3>
            <span className="ml-auto text-[11px] font-semibold text-ink/40">
              {closedCats.has(cat)
                ? `${rows.filter((e) => (e.value || {}).email || (e.value || {}).sms).length} of ${rows.length} on`
                : ""}
            </span>
          </button>
          {!closedCats.has(cat) && (
            <div>
              {rows.map((entry) => (
                <EventRow key={entry.key} entry={entry} verified={verified} optedOut={prefs.opted_out} onToggle={toggleEvent} />
              ))}
            </div>
          )}
        </section>
      ))}

      <QuietHours prefs={prefs} onSave={saveQuietHours} />

      <div className="rounded-2xl border border-sand-200 bg-sand-50 p-5 flex items-start gap-4" data-testid="notify-safety-note">
        <div className="h-10 w-10 rounded-xl bg-white border border-sand-200 flex items-center justify-center shrink-0">
          <ShieldAlert className="h-5 w-5 text-danger-600" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold">Urgent safety alerts</div>
          <div className="text-sm text-muted-foreground mt-0.5">
            Text alerts stay on — gas leaks, water shutoffs and other emergencies reach everyone. The only way out is replying STOP to all texts.
          </div>
        </div>
      </div>
    </div>
  );
}
